import type { FormikErrors } from 'formik';

import { Directive } from '../../types';

export type ConfirmSeverity = 'default' | 'warning' | 'error';

/** Key for the object-level validation error when both toggles are disabled */
export const AT_LEAST_ONE_ERROR_KEY = 'at-least-one-enabled' as const;

export type ChangeConfirmationFormErrors =
  FormikErrors<ChangeConfirmationFormValues> & {
    [AT_LEAST_ONE_ERROR_KEY]?: string;
  };

export function getFormLevelError(
  errors: FormikErrors<ChangeConfirmationFormValues>
): string | undefined {
  if (
    errors &&
    typeof errors === 'object' &&
    AT_LEAST_ONE_ERROR_KEY in errors
  ) {
    const value = (errors as Record<string, unknown>)[AT_LEAST_ONE_ERROR_KEY];
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

export interface ChangeConfirmationPolicyData {
  HighlightEnabled: Directive<boolean>;
  HighlightColor: Directive<string>;
  BannerMessage: Directive<string>;
  ConfirmEnabled: Directive<boolean>;
  ConfirmTitle: Directive<string>;
  ConfirmMessage: Directive<string>;
  ConfirmSeverity: Directive<ConfirmSeverity>;
}

export type ChangeConfirmationPolicyPayload = ChangeConfirmationPolicyData;

export interface ChangeConfirmationFormData {
  highlightEnabled: boolean;
  /** Required when highlightEnabled is true; otherwise optional per validation schema */
  highlightColor?: string;
  /** Optional per validation schema (no .required()) */
  bannerMessage?: string;
  confirmEnabled: boolean;
  /** Required when confirmEnabled is true; otherwise optional per validation schema */
  confirmTitle?: string;
  /** Required when confirmEnabled is true; otherwise optional per validation schema */
  confirmMessage?: string;
  confirmSeverity: ConfirmSeverity;
}

export interface ChangeConfirmationFormValues extends ChangeConfirmationFormData {
  name: string;
  environmentGroupIds: number[];
}

export const CHANGE_CONFIRMATION_INITIAL_VALUES: ChangeConfirmationFormData = {
  highlightEnabled: true,
  highlightColor: '#ffbbbb',
  bannerMessage: '',
  confirmEnabled: true,
  confirmTitle: '',
  confirmMessage: '',
  confirmSeverity: 'warning',
};

export const CHANGE_CONFIRMATION_FORM_INITIAL_VALUES: ChangeConfirmationFormValues =
  {
    name: '',
    environmentGroupIds: [],
    ...CHANGE_CONFIRMATION_INITIAL_VALUES,
  };

export function isChangeConfirmationFormData(
  data: unknown
): data is ChangeConfirmationFormData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'highlightEnabled' in data &&
    'confirmEnabled' in data
  );
}

export function isChangeConfirmationPolicyData(
  data: unknown
): data is ChangeConfirmationPolicyData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'HighlightEnabled' in data &&
    'ConfirmEnabled' in data
  );
}

/** Normalise unknown policy data to ChangeConfirmationPolicyData or null. Shared by hook and axios interceptor. */
export function getChangeConfirmationPolicyData(
  data: unknown
): ChangeConfirmationPolicyData | null {
  return data && isChangeConfirmationPolicyData(data) ? data : null;
}

export function buildChangeConfirmationPolicyPayload(
  values: ChangeConfirmationFormData
): ChangeConfirmationPolicyPayload {
  return {
    HighlightEnabled: { value: values.highlightEnabled, enforce: false },
    HighlightColor: {
      value: values.highlightEnabled ? (values.highlightColor ?? '') : '',
      enforce: false,
    },
    BannerMessage: {
      value: values.highlightEnabled ? (values.bannerMessage ?? '') : '',
      enforce: false,
    },
    ConfirmEnabled: { value: values.confirmEnabled, enforce: false },
    ConfirmTitle: {
      value: values.confirmEnabled ? (values.confirmTitle ?? '') : '',
      enforce: false,
    },
    ConfirmMessage: {
      value: values.confirmEnabled ? (values.confirmMessage ?? '') : '',
      enforce: false,
    },
    ConfirmSeverity: { value: values.confirmSeverity, enforce: false },
  };
}
